import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHelp } from '../context/HelpContext';
import { useAuth } from '../context/AuthContext';
import { tourSteps, getPageTour } from '../data/helpContent';

const PADDING = 8;

function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, el };
}

export default function HelpTour() {
  const { tourOpen, tourMode, closeTour } = useHelp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  // Pick the right step list based on mode + current path
  const visibleSteps = useMemo(() => {
    if (tourMode === 'welcome') {
      return tourSteps.filter(s => !s.roles || s.roles.includes(user?.role));
    }
    // page mode — use the page-specific tour, fall back to welcome if none
    const pageSteps = getPageTour(location.pathname);
    if (pageSteps && pageSteps.length) {
      return pageSteps.filter(s => !s.roles || s.roles.includes(user?.role));
    }
    return tourSteps.filter(s => !s.roles || s.roles.includes(user?.role));
  }, [tourMode, location.pathname, user]);

  const step = visibleSteps[idx];

  const recompute = useCallback(() => {
    if (!step) return;
    if (step.placement === 'center' || !step.target) {
      setRect(null);
      return;
    }
    setRect(getRect(step.target));
  }, [step]);

  useEffect(() => {
    if (!tourOpen) return;
    setIdx(0);
    // Welcome tour: ensure user is on dashboard so sidebar elements exist
    // Page tour: stay where we are
    if (tourMode === 'welcome' && location.pathname !== '/') {
      navigate('/');
    }
  }, [tourOpen, tourMode, navigate, location.pathname]);

  useEffect(() => {
    if (!tourOpen) return;
    recompute();
    const handler = () => recompute();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    const interval = setInterval(recompute, 300);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      clearInterval(interval);
    };
  }, [tourOpen, recompute]);

  if (!tourOpen || !step) return null;

  const isCenter = step.placement === 'center' || !rect;
  const isLast = idx === visibleSteps.length - 1;

  const tooltipStyle = (() => {
    if (isCenter) {
      return {
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        position: 'fixed',
      };
    }
    const placement = step.placement || 'bottom';
    const margin = 16;
    const tooltipW = 360;
    const tooltipH = 200;
    let top, left;
    if (placement === 'left') {
      top = rect.top + rect.height / 2 - tooltipH / 2;
      left = rect.left - tooltipW - margin;
    } else if (placement === 'right') {
      top = rect.top + rect.height / 2 - tooltipH / 2;
      left = rect.left + rect.width + margin;
    } else if (placement === 'top') {
      top = rect.top - tooltipH - margin;
      left = rect.left + rect.width / 2 - tooltipW / 2;
    } else {
      top = rect.top + rect.height + margin;
      left = rect.left + rect.width / 2 - tooltipW / 2;
    }
    top = Math.max(16, Math.min(window.innerHeight - tooltipH - 16, top));
    left = Math.max(16, Math.min(window.innerWidth - tooltipW - 16, left));
    return { top, left, position: 'fixed' };
  })();

  return (
    <>
      <div className="tour-backdrop" onClick={(e) => e.stopPropagation()}>
        {!isCenter && rect && (
          <div
            className="tour-spotlight"
            style={{
              top: rect.top - PADDING,
              left: rect.left - PADDING,
              width: rect.width + PADDING * 2,
              height: rect.height + PADDING * 2,
            }}
          />
        )}
      </div>

      <div className="tour-tooltip" ref={tooltipRef} style={tooltipStyle}>
        <div className="tour-step-counter">
          {tourMode === 'page' && '🎯 '}{idx + 1} / {visibleSteps.length}
        </div>
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          <button className="btn btn-outline btn-sm" onClick={closeTour}>تخطّى</button>
          <div style={{ display: 'flex', gap: 6 }}>
            {idx > 0 && (
              <button className="btn btn-outline btn-sm" onClick={() => setIdx(idx - 1)}>
                ‹ السابق
              </button>
            )}
            {!isLast ? (
              <button className="btn btn-primary btn-sm" onClick={() => setIdx(idx + 1)}>
                التالي ›
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={closeTour}>
                ابدأ الآن ✓
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
